import { useEffect, useState } from 'react';
import { Tabs } from 'expo-router';
import { api } from '@/api/client';
import React from 'react';
import { type ColorValue } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import OnaIcon, { type IconName } from '@/components/OnaIcon';
import { C, F } from '@/constants/theme';

const icon = (name: IconName) =>
  ({ color }: { color: ColorValue }) => <OnaIcon name={name} size={22} color={String(color)} />;

/* El «3» de la campanita estaba escrito a mano desde el prototipo: no contaba
   nada. Ahora es el número real de alertas, y de paso se pinta en el icono de
   la pantalla de inicio cuando el sistema lo permite (iOS lo admite en apps
   añadidas a la pantalla de inicio con permiso de notificaciones; si no puede,
   no pasa nada y se queda solo la campanita). */
async function pintaBadge(n: number) {
  try {
    const nav = navigator as unknown as { setAppBadge?: (x: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
    if (nav?.setAppBadge) { await (n > 0 ? nav.setAppBadge(n) : nav.clearAppBadge?.()); return; }
  } catch { /* seguimos por la vía nativa */ }
  try {
    const Notifications = await import('expo-notifications');
    await Notifications.setBadgeCountAsync(n);
  } catch { /* el lanzador no lo admite: no es un fallo */ }
}

function useAlertas(): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    let vivo = true;
    const leer = () => api.alertas().then((r) => {
      if (!vivo) return;
      /* 🔴 SOLO LO NO LEÍDO. Antes contaba TODO, así que la campanita se
         quedaba encendida para siempre y dejaba de querer decir «hay algo
         nuevo» (pedido de Patricio, 24-08). Una alerta sin `id` —el demo, o un
         servidor viejo— cuenta como no leída: mejor avisar de más que esconder. */
      const total = Array.isArray(r.data) ? r.data.filter((a) => !a.leido).length : 0;
      setN(total);
      /* 🔴 EL NÚMERO EN EL ICONO. `navigator.setAppBadge` es una API del
         NAVEGADOR: en la web instalada funciona, y dentro del APK no existe —
         por eso la campanita marcaba 12 y el icono no. En nativo hay que
         pedírselo al sistema por otra vía.
         Aviso honesto sobre Android: allí el número del icono depende del
         lanzador, y muchos solo pintan un punto cuando hay una notificación de
         verdad. Con las push reales (pendiente) se verá siempre; hasta
         entonces, en algunos móviles no saldrá y no es un fallo de la app. */
      pintaBadge(total);
    }).catch(() => {});
    leer();
    const t = setInterval(leer, 5 * 60e3);   // se refresca sin recargar la app
    return () => { vivo = false; clearInterval(t); };
  }, []);
  return n;
}

export default function TabLayout() {
  const alertas = useAlertas();
  /* 🔴 La barra medía 84 en iOS y 64 en TODO lo demás — incluida la web, que es
     precisamente donde corre en un iPhone. Resultado: en un 15 Pro Max las
     etiquetas se cortaban y quedaba un pasillo muerto debajo. Ahora la altura
     SALE del hueco real del dispositivo (el del indicador de inicio) en vez de
     adivinarse por plataforma; el tope de 24 evita que un hueco raro deje otra
     vez media pantalla vacía. */
  const insets = useSafeAreaInsets();
  /* 🔴 EL TOPE DE 24 ERA DEMASIADO BAJO Y EN ANDROID SE COMÍA LA BARRA. Lo puse
     para atajar el pasillo muerto del iPhone, pero el que arreglaba eso de
     verdad era el `100dvh` de +html.tsx. En Android la barra de botones del
     sistema mide unos 48 puntos, así que capando a 24 las etiquetas de las
     pestañas quedaban DEBAJO de ella: se veía «Pulse» y «Hoteles» a medias tras
     una franja blanca. Con 48 caben las dos cosas — el indicador del iPhone
     (34) y la barra de Android — y sigue habiendo un tope por si algún
     dispositivo devuelve un hueco absurdo. */
  return (
    <Tabs
      screenOptions={{
        /* 🔴 QUE LA BARRA SE APARTE AL ESCRIBIR. Patricio, 24-08: «el chat de
           Noa si bien sube no sube lo suficiente». Faltaba justo esto: la barra
           de pestañas seguía ocupando su sitio con el teclado abierto, así que
           la caja de texto quedaba tapada por ella. Con la barra fuera, la
           pantalla llega hasta abajo y el hueco del teclado ya cuadra. */
        tabBarHideOnKeyboard: true,
        headerShown: false,
        tabBarActiveTintColor: C.aqua,
        tabBarInactiveTintColor: C.muted,
        /* 🔴 NADA DE MÁRGENES PROPIOS ABAJO. Aquí había `height: 58 + abajo` y
           `paddingBottom: abajo`, sacados de `useSafeAreaInsets`. Pero la barra
           de pestañas de React Navigation YA aplica el área segura por su
           cuenta, así que se sumaba DOS VECES: en el iPhone quedaba un hueco
           muerto de casi 70 puntos bajo la barra (captura de Patricio, 25-08).
           En Android no se veía porque la barra del sistema está escondida y
           ahí ese margen vale cero — el fallo llevaba días delante y solo
           aparecía en un iPhone. */
        /* 🔴 DOS INTENTOS Y UN TÉRMINO MEDIO. Primero `58 + insets.bottom` con
           `paddingBottom: insets.bottom`, y en el iPhone quedaba un hueco grande
           bajo la barra. Luego lo quité del todo y salió PEOR: la barra se pegó
           al borde y el texto de las pestañas quedaba cortado por la mitad
           (captura de Patricio, 13:12). Un texto cortado es mucho peor que un
           hueco, así que se devuelve el margen — pero solo el necesario para el
           indicador de inicio, no el inset entero, que en algunos iPhone incluye
           más de lo que hace falta aquí. */
        tabBarStyle: {
          backgroundColor: 'rgba(10,30,45,0.96)',
          borderTopColor: 'rgba(140,200,215,0.13)',
          height: 58 + Math.min(insets.bottom, 24),
          paddingTop: 6,
          paddingBottom: Math.min(insets.bottom, 24),
        },
        tabBarLabelStyle: { fontFamily: F.uiSemi, fontSize: 10, letterSpacing: 0.3 },
        sceneStyle: { backgroundColor: C.bg },
      }}
    >
      <Tabs.Screen name="index" options={{ title: 'Pulse', tabBarIcon: icon('pulse') }} />
      <Tabs.Screen name="hoteles" options={{ title: 'Hoteles', tabBarIcon: icon('hoteles') }} />
      <Tabs.Screen name="ia" options={{ title: 'Noa', tabBarIcon: icon('ia') }} />
      <Tabs.Screen
        name="alertas"
        options={{
          title: 'Alertas',
          tabBarIcon: icon('alertas'),
          tabBarBadge: alertas > 0 ? alertas : undefined,
          tabBarBadgeStyle: { backgroundColor: C.bad, color: '#fff', fontFamily: F.uiBold, fontSize: 9 },
        }}
      />
    </Tabs>
  );
}
